import React, { useState } from 'react';
import { useConfig } from '@context/ConfigContext';
import PrivacyPolicy from '@components/Legal/PrivacyPolicy';
import TermsConditions from '@components/Legal/TermsConditions';
import styles from './Footer.module.css';

const Footer: React.FC = () => {
  const { config } = useConfig();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  if (!config || !config.components.footer.enabled) return null;

  const { footer } = config.components;
  const { business, contact, social } = config;
  const currentYear = new Date().getFullYear();

  const socialPlatforms = [
    { name: 'Facebook', url: social.facebook, icon: 'fab fa-facebook' },
    { name: 'Instagram', url: social.instagram, icon: 'fab fa-instagram' },
    { name: 'LinkedIn', url: social.linkedin, icon: 'fab fa-linkedin' },
    { name: 'Twitter', url: social.twitter, icon: 'fab fa-twitter' },
    { name: 'YouTube', url: social.youtube, icon: 'fab fa-youtube' },
  ].filter((platform) => platform.url);

  const navItems = [
    { label: 'Home', href: '#home', enabled: config.components.hero.enabled },
    { label: 'About', href: '#about', enabled: config.components.about.enabled },
    { label: 'Services', href: '#services', enabled: config.components.services.enabled },
    { label: 'Portfolio', href: '#portfolio', enabled: config.components.portfolio.enabled },
    { label: 'Testimonials', href: '#testimonials', enabled: config.components.testimonials.enabled },
    { label: 'FAQ', href: '#faq', enabled: config.components.faq.enabled },
    { label: 'Contact', href: '#contact', enabled: config.components.contact.enabled },
  ].filter((item) => item.enabled);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();

    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handlePrivacyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowPrivacy(true);
  };

  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowTerms(true);
  };

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.footerContent}>
            {/* About Column */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>About {business.name}</h3>
              <p className={styles.about}>{footer.about}</p>
              {business.showABN && business.abn && (
                <p className={styles.abn}>ABN: {business.abn}</p>
              )}
            </div>

            {/* Quick Links */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Quick Links</h3>
              <ul className={styles.linkList}>
                {navItems.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className={styles.link}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Contact Us</h3>
              <ul className={styles.contactList}>
                <li className={styles.contactItem}>
                  <i className="fas fa-phone" />
                  <a href={`tel:${contact.phoneRaw}`}>{contact.phone}</a>
                </li>
                <li className={styles.contactItem}>
                  <i className="fas fa-envelope" />
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </li>
                <li className={styles.contactItem}>
                  <i className="fas fa-map-marker-alt" />
                  <span>{contact.address}</span>
                </li>
              </ul>
            </div>

            {/* Social Media */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Follow Us</h3>
              {socialPlatforms.length > 0 ? (
                <div className={styles.socialIcons}>
                  {socialPlatforms.map((platform) => (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={platform.name}
                      className={styles.socialIcon}
                    >
                      <i className={platform.icon} />
                    </a>
                  ))}
                </div>
              ) : (
                <p className={styles.noSocial}>Connect with us</p>
              )}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={styles.bottomBar}>
            <div className={styles.copyright}>
              <p>&copy; {currentYear} {business.name}. All rights reserved.</p>
            </div>
            <div className={styles.legalLinks}>
              <a 
                href="#privacy" 
                onClick={handlePrivacyClick} 
                className={styles.legalLink}
              >
                Privacy Policy
              </a>
              <span className={styles.separator}>|</span>
              <a 
                href="#terms" 
                onClick={handleTermsClick} 
                className={styles.legalLink}
              >
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
        <div className="sr-only">
          <h2>Web Design Services - Mid North Coast NSW</h2>
          <p>Dan White Web Solutions provides affordable web design and website development services for small businesses, sole traders, and local businesses across regional New South Wales. We specialize in static website design, single page applications (SPA), and custom website development.</p>
          
          <h3>Service Areas - Local Web Design Near You</h3>
          <p>Proudly serving Bellingen, Coffs Harbour, Urunga, Dorrigo, Armidale, Nambucca, and surrounding Mid North Coast communities. Local web designer based in Bellingen NSW.</p>
          <ul>
            <li>Web design Bellingen - Professional website design services in Bellingen NSW</li>
            <li>Web designer Coffs Harbour - Affordable web development Coffs Harbour and surrounding areas</li>
            <li>Website design Urunga - Custom websites for Urunga businesses</li>
            <li>Web design Dorrigo - Modern website solutions for Dorrigo region</li>
            <li>Website developer Armidale - Static and custom websites Armidale NSW</li>
            <li>Web design Nambucca - Professional web services Nambucca Heads and Nambucca Valley</li>
            <li>Web design near me - Local web designer serving Mid North Coast NSW</li>
            <li>Web designer near me - Regional NSW web design services</li>
          </ul>
          
          <h3>Our Services</h3>
          <p>Website design Bellingen, web development Coffs Harbour, affordable websites NSW, static website development, single page websites, small business web design, web design for sole traders, professional website services, custom website development, responsive web design, mobile-friendly websites, regional NSW web designer, local web design services, web design Mid North Coast.</p>
          
          <h3>Why Choose Dan White Web Solutions</h3>
          <p>Affordable pricing from $1500 + GST, no ongoing maintenance required, fast turnaround, independent web designer, websites that work, professional web solutions, modern web design, local support, regional NSW specialist, web design near you.</p>
          
          <h3>Contact Your Local Web Designer</h3>
          <p>Phone: 0432 430 287 | Email: danwhitewebsolutions@outlook.com | ABN: 96 770 057 153 | Address: 14 Sky Place, Bellingen NSW 2454</p>
        </div>
      </footer>

      {/* Legal Modals */}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}
    </>
  );
};

export default Footer;